import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { ADD_USER_SETTINGS, UPDATE_USER_SETTINGS } from "../../../graphql/user";
import { Button } from "@mui/material";
import { Drawer, TextField, TransferList } from "@outpave/ds";
import type { TransferListItem } from "@outpave/ds";
import addUserAnalyticSettings from "validations/addUserAnalyticSettings";

/** One report column, in the order the report prints it. */
export interface ReportTemplateColumn {
  key?: string | null;
  name: string;
  parentId?: string | null;
  parentName?: string | null;
  index?: number | null;
}

/** A saved set of report columns. */
export interface ReportTemplate {
  id: string;
  name: string;
  settingData: ReportTemplateColumn[];
}

interface AnalyticTransactionSettingsDrawerProps {
  open: boolean;
  handleSuccess: (template: ReportTemplate) => void;
  handleRemoveSettings?: () => void;
  handleCloseDrawer: (isRefetchData?: boolean) => void;
  /** The template being edited, or null to create one. */
  selectedSettingData?: ReportTemplate | null;
  /** Columns the template can add: every default column it does not hold yet. */
  defaultColumns?: ReportTemplateColumn[];
  /** The parent is acting on this template, such as deleting it; the drawer waits for it. */
  busy?: boolean;
}

export interface FormikAddNewAnalyticSetting {
  settingName: string;
}

interface ColumnLists {
  left: TransferListItem[];
  right: TransferListItem[];
}

// Column names are unique, so each is its own id; they are server data, never translated.
const toItems = (names: string[]): TransferListItem[] =>
  names.map((name) => ({ id: name, label: name }));

const AnalyticTransactionSettingsDrawer: React.FC<
  AnalyticTransactionSettingsDrawerProps
> = (props) => {
  const {
    open,
    handleSuccess,
    handleCloseDrawer,
    selectedSettingData,
    defaultColumns,
    handleRemoveSettings,
    busy = false,
  } = props;

  const [isSavingSettings, setIsSavingSettings] = useState<boolean>(false);
  const working = isSavingSettings || busy;
  const [columns, setColumns] = useState<ColumnLists>({ left: [], right: [] });

  const [updateUserSettings] = useMutation(UPDATE_USER_SETTINGS, {
    onCompleted: ({ updateUserSettings }) => {
      handleSuccess(updateUserSettings);
      formik.resetForm();
      toast.success(`${t("analyticSettings.settingsUpdated")}`);
      setIsSavingSettings(false);
      handleCloseDrawer(true);
    },
    onError: (error) => {
      setIsSavingSettings(false);
      toast.error(error.message);
    },
  });

  const [addUserSettings] = useMutation(ADD_USER_SETTINGS, {
    onCompleted: ({ addUserSettings }) => {
      handleSuccess(addUserSettings);
      formik.resetForm();
      toast.success(`${t("analyticSettings.settingsAdded")}`);

      setIsSavingSettings(false);
      handleCloseDrawer(true);
    },
    onError: (error) => {
      setIsSavingSettings(false);
      toast.error(error.message);
    },
  });

  const formik = useFormik<FormikAddNewAnalyticSetting>({
    initialValues: {
      settingName: "",
    },
    validationSchema: addUserAnalyticSettings,
    onSubmit: (values) => {
      setIsSavingSettings(true);
      const { settingName } = values;

      let input = {
        name: settingName,
        data: columns.right.map((item, index) => ({
          name: item.id,
          index: index,
        })),
      };
      if (selectedSettingData?.id) {
        updateUserSettings({
          variables: {
            userSettingType: "ANALYTICS_TRANSACTION",
            input: {
              ...input,
              id: selectedSettingData.id,
            },
          },
        });
      } else {
        addUserSettings({
          variables: {
            userSettingType: "ANALYTICS_TRANSACTION",
            input: input,
          },
        });
      }
    },
  });

  const { t } = useTranslation();

  const initialSelectedColumns = useMemo<string[]>(
    () =>
      selectedSettingData
        ? selectedSettingData.settingData.map((item) => item.name)
        : (defaultColumns?.map((item) => item.name) ?? []),
    [selectedSettingData, defaultColumns],
  );

  useEffect(() => {
    if (!open) return;
    formik.setFieldValue("settingName", selectedSettingData?.name ?? "");
    setColumns({
      left: toItems(
        selectedSettingData
          ? (defaultColumns?.map((item) => item.name) ?? [])
          : [],
      ),
      right: toItems(initialSelectedColumns),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSelectedColumns]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (working) return;
        handleCloseDrawer();
      }}
      title={
        selectedSettingData
          ? t("analyticSettings.reportTemplate")
          : t("analyticSettings.addNewReportTemplate")
      }
      width="544px"
      loading={working}
      footer={
        <>
          <Button
            variant="primary-outlined"
            disabled={working}
            onClick={() => {
              handleCloseDrawer();
            }}
          >
            {t("Back")}
          </Button>
          <Button
            variant="primary-filled"
            disabled={working}
            onClick={() => formik.handleSubmit()}
          >
            {t("Save")}
          </Button>
          {handleRemoveSettings && selectedSettingData && (
            <Button
              variant="danger-filled"
              disabled={working}
              onClick={() => {
                handleRemoveSettings();
              }}
            >
              {t("Delete")}
            </Button>
          )}
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <TextField
          label={t("analyticSettings.nameOfSetting")}
          name="settingName"
          placeholder={t("analyticSettings.defaultSettings") as string}
          fullWidth
          value={formik.values.settingName}
          onChange={formik.handleChange}
          errorText={
            formik.errors["settingName"] && formik.touched["settingName"]
              ? (formik.errors["settingName"] as string)
              : undefined
          }
        />
      </form>

      <TransferList
        left={columns.left}
        right={columns.right}
        onChange={setColumns}
        leftTitle={t("Available Columns")}
        rightTitle={t("Selected Columns")}
        reorderable
        listMaxHeight="none"
        sx={{ flex: 1, minHeight: 240 }}
        labels={{
          moveAllRight: t("Move all right"),
          moveSelectedRight: t("Move selected right"),
          moveSelectedLeft: t("Move selected left"),
          moveAllLeft: t("Move all left"),
          moveSelectedUp: t("Move selected up"),
          moveSelectedDown: t("Move selected down"),
          selectAll: (title) => t("Select all {{title}}", { title }),
          count: (checked, total) =>
            t("{{checked}}/{{total}} selected", { checked, total }),
          empty: t("No columns"),
        }}
      />
    </Drawer>
  );
};

export default AnalyticTransactionSettingsDrawer;
